// Blog cockpit — English. Must satisfy the Katalog shape derived from de/blog.ts
// (a missing or extra key is a tsc error). benefit_area names stay raw (DB data).
export const blog = {
  title: "Blog cockpit",
  reload: "Reload",
  loading: "Loading blog cockpit …",

  stats: {
    topics: "topics",
    published: "published",
    blocked: "blocked",
    trendsToWrite: "trends to write",
  },

  tabs: {
    backlog: "Articles",
    claims: "Claims",
    moa: "MoA",
    trends: "Trend inbox",
    evidence: "Evidence review",
  },

  status: {
    backlog: "Backlog",
    planned: "Planned",
    generating: "Generating",
    draft: "Draft",
    review: "Review",
    approved: "Approved",
    published: "Published",
  },

  topicClass: {
    basis: "Base",
    kuer: "Trend",
  },

  backlog: {
    intro: "Search-intent topics per benefit area. Base = from evidence, Trend = from a radar trend.",
    empty: "No backlog yet",
    emptyHint: "Once the search-intent backlog (blog_topic) is seeded, topics appear here per benefit area.",
    topicsCount: (n: number) => `${n} ${n === 1 ? "topic" : "topics"}`,
  },

  card: {
    eviClaims: (n: number) => `${n} ${n === 1 ? "proof" : "proofs"}`,
    noEvidence: "no evidence",
    blocked: "blocked",
    keyword: "Keyword",
    duplicate: "duplicate",
    duplicateHint: "Merge proposal (N10): same claim pool or same search intent as another topic.",
  },

  articleType: {
    ingredient_deep_dive: "Deep dive",
    mechanism_explainer: "Mechanism",
    formulation_guide: "Formulation guide",
    claim_benefit_guide: "Claim guide",
    comparison: "Comparison",
    study_digest: "Study digest",
    method_explainer: "Method",
    regulatory_certification: "Regulatory",
    trend_market: "Trend",
    problem_skin_type: "Problem / skin type",
  },

  topic: {
    back: "Back to cockpit",
    eyebrow: "Search-intent topic",
    evidenceCore: "Evidence core",
    evidenceHint: "The significance-gated evi claims that back this topic.",
    certificates: "Certificates",
    certificatesHint: "Mentionable as tags where they fit the topic — never the article subject.",
    pipeline: "Pipeline",
    generate: "Generate article",
    generating: "Writing article … (~15–40 s)",
    openArticle: "Open article",
    regenerate: "Regenerate",
    noArticleYet: "No article generated yet.",
    notFound: "Topic not found",
    notFoundHint: "This topic does not exist (anymore).",
    generateFailed: "Generation failed",
    fromTrend: "from trend",
    blockedReason: "Blocker",
    noEvidenceWarn: "No article without an evi claim — this topic has no evidence linked.",
  },

  claims: {
    intro: "Released claims only — the cards released on the Evidence board. Drop and safety cards never appear here.",
    all: "All",
    fullReview: "Full review",
    presentation: "Presentation",
    empty: "No claims yet",
    emptyHint: "Release a card on the Evidence board to show it here.",
    count: (n: number) => `${n}`,
    caveat: "Caveat",
  },

  moa: {
    intro: "The mechanism-of-action layer — how the molecule works, per benefit area. Footnotes and provenance sit on every card.",
    all: "All",
    empty: "No MoA cards yet",
    emptyHint: "Once the MoA cards are seeded, the mechanism gallery appears here.",
  },

  // Evidence board (Spec evidence-board v2 §4b) — gallery of the 46 claim candidates.
  // Release gate: use/use_framed released by default (expert can withdraw), drop/safety
  // always internal. effect areas come translated from the server (effect_area_en).
  evidence: {
    intro:
      "One card per claim candidate: the proposed slice of the study as a chart plus one sentence. Released cards may be used for claims; internal cards are for the Atlas overview only.",
    empty: "No candidates yet",
    emptyHint: "Once evi_claim_candidate is seeded (python -m app.scripts.load_evi_claim_candidates), the cards appear here.",
    emptyFiltered: "No cards for these filters.",
    count: (shown: number, total: number) =>
      `${shown} of ${total} cards${shown !== total ? " (filtered)" : ""}`,
    filterRelease: "Status",
    filterClass: "Class",
    filterArea: "Effect area",
    reset: "Reset",
    groups: { released: "Released", internal: "Internal" },
    groupHint: {
      released: "Use and use (framed) — may be used for claims.",
      internal: "Safety statements, drops and withdrawn cards — not for claims.",
    },
    cls: { use: "Use", use_framed: "Use (framed)", use_qualitative: "Use (qualitative)", safety: "Safety statement", drop: "Drop" },
    release: { released: "Released", internal: "Internal" },
    internalMarker: "Internal — not for claims",
    withdrawn: "withdrawn by expert",
    sourceNotNameable: "Source not nameable",
    toggleOn: "Released for claims",
    toggleOff: "Withdrawn (internal)",
    locked: "Internal by rule — cannot be released",
    releasedToast: "Card released",
    withdrawnToast: "Card withdrawn — now internal",
    saveFailed: "Could not save",
    lastBy: (who: string, when: string | null) =>
      `${who}${when ? ` · ${new Date(when).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}`,
    comment: "Comment",
    commentPlaceholder: "Comment (optional)",
    saveComment: "Save comment",
    commentSaved: "Comment saved",
    svgFailed: "Card could not be loaded",
    proof: "Proof",
    shownOmitted: (shown: number, omitted: number) => `${shown} shown · ${omitted} omitted`,
    source: "Source",
    design: "Design",
    shownRows: (n: number) => `Shown rows (${n})`,
    omittedRows: (n: number) => `Omitted rows with reason (${n})`,
    loading: "Loading …",
    loadFailed: "Could not load the omitted rows",
    keyNumber: "Key number (calculated)",
    roundedDown: "rounded down",
    caveats: "Notes & caveats",
    caveatsOriginal: "Expert note (original language)",
    figures: "Study figures",
    downloads: "Downloads",
    cardSvg: "Card SVG",
    cardPng: "Card PNG",
    chartSvg: "Chart SVG",
    chartPng: "Chart PNG",
  },

  trends: {
    intro: "Topics surfaced by the trend radar. “Write” turns one into a trend topic in the backlog.",
    refresh: "Refresh radar",
    refreshing: "Radar running …",
    refreshDone: (n: number) => `Radar done — ${n} ${n === 1 ? "trend" : "trends"}`,
    refreshFailed: "Radar run failed",
    filter: {
      all: "All",
      watch: "Watch",
      write: "Write",
      dismissed: "Dismissed",
    },
    write: "Write",
    watch: "Watch",
    dismiss: "Dismiss",
    open: "Open trend topic",
    writeGateHint: "No evi backing in this benefit area — a full article isn't possible.",
    notMapped: "not mapped to a benefit area",
    groundable: "evi-backed",
    momentum: "Momentum",
    sources: "Sources",
    empty: "No trends",
    emptyHint: "Run the radar to surface trend topics.",
    statusUpdated: "Status updated",
    writePromoted: "Added as a trend topic in the backlog",
    statusFailed: "Could not set status",
  },

  seo: {
    title: "SEO & publishing",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    saved: "Saved.",
    saveFailed: "Save failed",
    subtitle: "Subtitle",
    slug: "Slug",
    meta: "Meta description",
    chars: (n: number) => `${n} characters`,
    primaryKeyword: "Primary keyword",
    byline: "Byline",
    reviewedBy: (r: string) => `reviewed by ${r}`,
    cta: "Closing CTA",
    ctaMissing: "missing",
    lintNumbers: (n: number) => `${n} number(s) not in the writer inputs`,
    lintOk: "All numbers backed by writer inputs",
    lintAnchors: (n: number) => `${n} topic anchor(s) not released, not used`,
    absent: "(not set)",
  },

  cert: {
    certified: "Certified",
    selfDeclared: "Self-declared",
    dataStatement: "Data/statement",
    validUntil: (d: string) => `valid until ${d}`,
  },
};
